import { Directionality } from '@angular/cdk/bidi';
import {
  DOWN_ARROW,
  LEFT_ARROW,
  PAGE_DOWN,
  PAGE_UP,
  RIGHT_ARROW,
  UP_ARROW,
} from '@angular/cdk/keycodes';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { debounceTime } from 'rxjs/operators';

import { dragObservable } from './drag-observable';
import { mouseoverRelativeCoordinatesObservable } from './mouseover-relative-coordinates-observable';

@Component({
  selector: 'bm-media-progress-slider',
  templateUrl: 'media-progress-slider.component.html',
  styleUrls: ['media-progress-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Media progress slider
 */
export class MediaProgressSliderComponent {
  @HostBinding('attr.aria-valuemax')
  @Input()
  public duration!: number;

  @HostBinding('attr.aria-valuenow')
  @Input()
  public set value(v: number) {
    this._value = v;
  }

  public get value() {
    return this._value;
  }

  @HostBinding('attr.aria-valuemin')
  @Input()
  public valuemin = 0;

  @HostBinding('attr.tabindex')
  @Input()
  public tabindex = 0;

  @HostBinding('attr.role') public readonly role = 'slider';

  @Output() public seek = new EventEmitter<number>();

  public pointerTime!: number;

  @ViewChild('doneBar', { read: ElementRef, static: true })
  private _doneBar!: ElementRef<HTMLElement>;
  @ViewChild('previewBar', { read: ElementRef, static: false })
  private _previewBar!: ElementRef<HTMLElement>;

  private _isDragging = false;
  private _isOver = false;
  private _value!: number;

  constructor(
    public _elementRef: ElementRef<HTMLElement>,
    // tslint:disable-next-line:no-any
    @Inject(DOCUMENT) private _document: any,
    private _cdr: ChangeDetectorRef,
    private _dir: Directionality,
  ) {}

  @HostListener('mouseenter', ['$event'])
  public onMouseenter($event: MouseEvent) {
    this._isOver = true;

    mouseoverRelativeCoordinatesObservable(
      $event,
      this._document,
      this._elementRef.nativeElement,
    ).subscribe({
      next: val => {
        this.pointerTime = val * (this.duration || 0);
        const overTime = Math.max(
          0,
          this.getRelativeLeft(this.pointerTime) -
            this.getRelativeLeft(this.value),
        );
        if (
          overTime > 0 &&
          this._previewBar &&
          this._previewBar.nativeElement
        ) {
          const relativeLeft = this.getRelativeLeft(this.value);
          this._previewBar.nativeElement.style.left = `${relativeLeft}px`;
          this._previewBar.nativeElement.style.width = `${overTime}px`;
        }
        this._cdr.markForCheck();
      },
      error: () => {
        this._isOver = false;
      },
      complete: () => {
        this._isOver = false;
        this._cdr.markForCheck();
      },
    });
  }

  @HostListener('click', ['$event'])
  public onClick(e: MouseEvent) {
    if (!this._isDragging) {
      e.preventDefault();
      const { offsetX } = e;
      const { width } = this._elementRef.nativeElement.getBoundingClientRect();
      const percent = offsetX / width;
      this.seek.emit(percent * (this.duration || 0));
    }
  }

  // tslint:disable-next-line:cyclomatic-complexity
  @HostListener('keydown', ['$event'])
  public onKeydown(e: KeyboardEvent) {
    let emitChanges = false;
    if (this.duration) {
      const bigStep = 0.1 * this.duration;

      switch (e.keyCode) {
        case DOWN_ARROW: {
          e.preventDefault();
          this._value = Math.max(0, this._value - 1);
          emitChanges = true;
          break;
        }
        case UP_ARROW: {
          e.preventDefault();
          this._value = Math.min(this.duration, this._value + 1);
          emitChanges = true;
          break;
        }
        case LEFT_ARROW: {
          e.preventDefault();
          this._value = Math.min(
            Math.max(0, this._value + (this._dir.value === 'ltr' ? -1 : 1)),
            this.duration,
          );
          emitChanges = true;
          break;
        }
        case RIGHT_ARROW: {
          e.preventDefault();
          this._value = Math.min(
            Math.max(0, this._value + (this._dir.value === 'ltr' ? 1 : -1)),
            this.duration,
          );
          emitChanges = true;
          break;
        }

        case PAGE_UP:
          e.preventDefault();
          this._value = Math.max(0, this._value + bigStep);
          emitChanges = true;
          break;
        case PAGE_DOWN:
          e.preventDefault();
          this._value = Math.max(0, this._value - bigStep);
          emitChanges = true;
          break;
        default:
      }
    }
    if (emitChanges) {
      this.seek.emit(this.value);
    }
  }

  public getRelativeLeft(value: number) {
    if (this._elementRef.nativeElement && value && this.duration) {
      const { width } = this._elementRef.nativeElement.getBoundingClientRect();
      const relative = value / (this.duration - this.valuemin);
      return relative * width;
    }
    return 0;
  }

  public showThumb() {
    return this._isDragging || this._isOver;
  }

  public showPreviewBar() {
    return this._isOver && !this._isDragging;
  }

  public onThumbMousedown($event: MouseEvent) {
    this._cdr.detach();
    this._isDragging = true;
    const dragObservable$ = dragObservable(
      $event,
      this._document,
      this._elementRef.nativeElement,
    );

    // The two observables with different throttles target components
    // that should update at different rates. The bounced targets the
    // the time tooltip, while the non bounced targets the dragged thumb
    // and bars.
    dragObservable$
      .pipe(debounceTime(15))
      .subscribe(() => this._cdr.detectChanges());

    dragObservable$.subscribe({
      next: val => {
        this._value = val * (this.duration || 0);
        const relativeLeft = this.getRelativeLeft(this.value);
        ($event.target as HTMLElement).style.left = `${relativeLeft}px`;
        if (this._doneBar && this._doneBar.nativeElement) {
          this._doneBar.nativeElement.style.width = `${relativeLeft}px`;
        }
        this.seek.emit(this.value);
      },
      error: () => {
        this._cdr.reattach();
      },
      complete: () => {
        this._cdr.reattach();
        setTimeout(() => {
          this._isDragging = false;
        }, 10);
      },
    });
  }
}
